import React, { createContext, useContext, useReducer, useEffect, useMemo, ReactNode } from 'react';
import { authService, User } from '../services/userService';
import { clearApiCache } from '../services/apiService';
import { refreshEntityDateFormat } from '../utils/dateSettings';

/**
 * Authentication Context for managing user sessions and authentication state
 * 
 * This context provides:
 * - User login/logout functionality
 * - Session persistence with localStorage
 * - Authentication state management across components
 * - Automatic session restoration on app load
 * 
 * @author AnyGRC System
 * @created 2025-01-27
 */

// Authentication state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionExpiry: Date | null;
}

// Authentication action types
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; sessionExpiry?: Date } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'RESTORE_SESSION'; payload: { user: User; sessionExpiry?: Date } }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SESSION_EXPIRED' };

// Authentication context interface
interface AuthContextType {
  state: AuthState;
  login: (login_id: string, password: string, rememberMe?: boolean, role?: string, entityId?: number, module?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  checkSession: () => Promise<boolean>;
  isSessionExpired: () => boolean;
  /**
   * Plan 04-02 (DRC-05) — W4 lock: token-only bootstrap helper used by the
   * post-set-password auto-login flow. Writes the supplied JWT to the same
   * sessionStorage key apiClient's interceptor reads, then fetches /me and
   * dispatches RESTORE_SESSION so the rest of the app sees the user. Returns
   * true on success, false on failure (caller may navigate to /login).
   */
  bootstrapFromToken: (token: string) => Promise<boolean>;
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start loading to allow session restore
  error: null,
  sessionExpiry: null,
};

// Authentication reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        sessionExpiry: action.payload.sessionExpiry || null,
      };

    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        sessionExpiry: null,
      };

    case 'LOGOUT':
    case 'SESSION_EXPIRED':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        sessionExpiry: null,
      };

    case 'RESTORE_SESSION':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        sessionExpiry: action.payload.sessionExpiry || null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Storage keys for session persistence.
//
// PER-TAB ISOLATION: the whole session (token + user + role + expiry) lives in
// sessionStorage, which is scoped to a single browser tab, survives page
// refresh, and is discarded when the tab closes. Two tabs therefore hold two
// fully independent sessions — you can be logged in as different users / into
// different entities in each tab at the same time. There is deliberately NO
// localStorage persistence and NO "Remember Me" (that would share one session
// across every tab and break the isolation).
const STORAGE_KEYS = {
  USER: 'anygrc_user',
  SESSION_EXPIRY: 'anygrc_session_expiry',
  USER_ROLE: 'anygrc_user_role',
} as const;

// Legacy localStorage keys from the old shared-session design. We proactively
// scrub them so a stale localStorage token can never leak into a fresh tab.
const LEGACY_LOCAL_KEYS = [
  'anygrc_auth_token', 'anygrc_user', 'anygrc_session_expiry', 'anygrc_user_role',
  'anygrc_remember_me', 'anygrc_remembered_login_id', 'anygrc_remembered_password',
  'anygrc_remembered_entity_id',
];

// Helper functions for session storage (sessionStorage-only, per tab).
const SessionStorage = {
  saveSession: (user: User, sessionExpiry?: Date) => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      if (sessionExpiry) {
        sessionStorage.setItem(STORAGE_KEYS.SESSION_EXPIRY, sessionExpiry.toISOString());
      }
    } catch (error) {
      console.warn('Failed to save session to storage:', error);
    }
  },

  loadSession: (): { user: User | null; sessionExpiry: Date | null } => {
    try {
      const userStr = sessionStorage.getItem(STORAGE_KEYS.USER);
      const sessionExpiryStr = sessionStorage.getItem(STORAGE_KEYS.SESSION_EXPIRY);
      const role = sessionStorage.getItem(STORAGE_KEYS.USER_ROLE) as 'GRC Admin' | 'Organisation Member' | 'Audit User' | null;

      const user = userStr ? JSON.parse(userStr) : null;

      // Add role to user object if it exists
      if (user && role) {
        user.role = role;
      }

      const sessionExpiry = sessionExpiryStr ? new Date(sessionExpiryStr) : null;

      return { user, sessionExpiry };
    } catch (error) {
      console.warn('Failed to load session from storage:', error);
      return { user: null, sessionExpiry: null };
    }
  },

  clearSession: () => {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.USER);
      sessionStorage.removeItem(STORAGE_KEYS.SESSION_EXPIRY);
      sessionStorage.removeItem(STORAGE_KEYS.USER_ROLE);
      sessionStorage.removeItem('anygrc_auth_token');

      // Scrub any legacy shared-session remnants from localStorage.
      LEGACY_LOCAL_KEYS.forEach(k => localStorage.removeItem(k));

      // Clear theme/role data on logout
      localStorage.removeItem('anygrc_theme');
      localStorage.removeItem('anygrc_role_theme');
    } catch (error) {
      console.warn('Failed to clear session storage:', error);
    }
  },
};

// AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if session is expired
  const isSessionExpired = (): boolean => {
    if (!state.sessionExpiry) return false;
    return new Date() > state.sessionExpiry;
  };

  // Check session validity with backend
  const checkSession = async (): Promise<boolean> => {
    try {
      const session = await authService.getSession();

      if (!session.logged_in) {
        dispatch({ type: 'SESSION_EXPIRED' });
        SessionStorage.clearSession();
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Session check failed:', error);
      dispatch({ type: 'SESSION_EXPIRED' });
      SessionStorage.clearSession();
      return false;
    }
  };

  // Login function
  const login = async (login_id: string, password: string, rememberMe: boolean = false, role?: string, entityId?: number, module?: string): Promise<boolean> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const user = await authService.login({ login_id, password, entity_id: entityId, module });

      if (user) {
        // Calculate session expiry (24 hours from now)
        const sessionExpiry = new Date();
        sessionExpiry.setHours(sessionExpiry.getHours() + 24);

        // Add numeric role to user object if provided
        const roleNumber = role ? parseInt(role, 10) as 1 | 2 | 3 | 4 : undefined;
        const userWithRole: User = roleNumber ? { ...user, role: roleNumber } : user;

        // Ensure entity_id is in user object (redundant if authService does it, but safe)
        if (entityId && !userWithRole.entity_id) {
          userWithRole.entity_id = entityId;
        }

        // Ensure roles array is present (if backend sends it)
        if (user.roles) {
          userWithRole.roles = user.roles;
        }

        // Portal access is gated server-side by DERIVED workspaces — backend
        // /auth/login computes the offered workspaces from the activities the
        // user's roles grant in this entity (USR_activities.workspace, chapter-20
        // §20.4) and returns 403 when the chosen workspace is not among them.
        // The derived set is also returned on /auth/me as user.workspaces. No
        // stored USR_user_entity_map.is_* flag is consulted.

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: userWithRole, sessionExpiry }
        });

        // Save to storage (save userWithRole, not original user).
        // sessionStorage only — per-tab isolation. `rememberMe` is intentionally
        // ignored: cross-tab persistence would defeat per-tab sessions.
        SessionStorage.saveSession(userWithRole, sessionExpiry);

        // Clear API cache after successful login to refresh data
        clearApiCache();

        return true;
      } else {
        dispatch({ type: 'LOGIN_FAILURE', payload: 'Login failed' });
        return false;
      }
    } catch (error: any) {
      // Prefer the FastAPI `detail` over the generic axios "Request failed with status code XXX".
      const detail = error?.response?.data?.detail;
      const errorMessage =
        (typeof detail === 'string' && detail) ||
        (Array.isArray(detail) && detail[0]?.msg) ||
        (error instanceof Error ? error.message : 'Login failed');
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return false;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
      SessionStorage.clearSession();
      clearApiCache();
    }
  };

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Plan 04-02 (DRC-05) — W4 bootstrap from raw JWT (option-a).
  const bootstrapFromToken = async (token: string): Promise<boolean> => {
    try {
      // Write to the same storage key apiClient's request interceptor reads
      // (apiService.ts:26). Once written, getSession()'s GET /me will carry
      // the Authorization header automatically.
      sessionStorage.setItem('anygrc_auth_token', token);
      const session = await authService.getSession();
      if (!session.logged_in || !session.user) {
        sessionStorage.removeItem('anygrc_auth_token');
        return false;
      }
      const sessionExpiry = new Date();
      sessionExpiry.setHours(sessionExpiry.getHours() + 24);
      SessionStorage.saveSession(session.user, sessionExpiry);
      dispatch({
        type: 'RESTORE_SESSION',
        payload: { user: session.user, sessionExpiry },
      });
      return true;
    } catch (err) {
      console.warn('bootstrapFromToken failed:', err);
      sessionStorage.removeItem('anygrc_auth_token');
      return false;
    }
  };

  // Restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      const { user, sessionExpiry } = SessionStorage.loadSession();

      if (user) {
        // Check if session is expired
        if (sessionExpiry && new Date() > sessionExpiry) {
          console.log('Session expired, clearing storage');
          SessionStorage.clearSession();
          dispatch({ type: 'SESSION_EXPIRED' }); // Ensure loading stops
          return;
        }

        // Verify session with backend
        const isValid = await checkSession();

        if (isValid) {
          dispatch({
            type: 'RESTORE_SESSION',
            payload: { user, sessionExpiry: sessionExpiry || undefined }
          });
          console.log('Session restored successfully');
        } else {
          dispatch({ type: 'SESSION_EXPIRED' }); // Ensure loading stops if invalid
        }
      } else {
        dispatch({ type: 'SESSION_EXPIRED' }); // Ensure loading stops if no user
      }
    };

    restoreSession();
  }, []);

  // Load the entity's display settings (date format) once the session is live
  // so read-only dates render per Entity Settings for every persona.
  useEffect(() => {
    if (state.isAuthenticated) { void refreshEntityDateFormat(); }
  }, [state.isAuthenticated]);

  // Auto-logout on session expiry
  useEffect(() => {
    if (state.isAuthenticated && state.sessionExpiry) {
      const checkExpiry = () => {
        if (isSessionExpired()) {
          console.log('Session expired, logging out...');
          logout();
        }
      };

      // Check every minute
      const interval = setInterval(checkExpiry, 60000);

      return () => clearInterval(interval);
    }
  }, [state.isAuthenticated, state.sessionExpiry]);

  // Idle/inactivity auto-logout (ANYGRC_Vulnerabilities: "Insufficient session
  // Management"). The expiry check above only enforces the token's absolute
  // 8h lifetime — a session left open and untouched stayed valid for the
  // full 8 hours regardless of activity. This tracks real user input and
  // logs out after IDLE_TIMEOUT_MS of no activity, independent of the token's
  // own expiry. The backend enforces the same limit server-side
  // (utils/auth.py: check_and_touch_session / usr_active_sessions), so a
  // captured token can't be replayed indefinitely just because the browser
  // tab went idle — this client-side timer is what gives the user immediate,
  // visible feedback rather than a bare 401 on their next click.
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes — matches backend IDLE_TIMEOUT_MINUTES
    let idleTimer: ReturnType<typeof setTimeout>;

    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        console.log('Session idle timeout reached, logging out...');
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(evt => window.addEventListener(evt, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
    };
  }, [state.isAuthenticated]);

  // Session Transfer Mechanism (Multi-Tab Support)
  // NOTE: the old cross-tab "Session Transfer Mechanism" was removed on purpose.
  // It copied an authenticated session from an existing tab into any new tab via
  // localStorage `storage` events, which is the exact opposite of per-tab
  // isolation. Each tab now bootstraps its own session from its own
  // sessionStorage (see restoreSession above) and stays independent.

  // PERF: memoize so consumers of useAuth() don't re-render on every
  // AuthProvider re-render. login/logout/clearError/checkSession/
  // isSessionExpired/bootstrapFromToken are stable closures (defined inside
  // the provider but not recreated by React on each render via reducer
  // pattern); only `state` changes drive a new contextValue.
  const contextValue: AuthContextType = useMemo(
    () => ({ state, login, logout, clearError, checkSession, isSessionExpired, bootstrapFromToken }),
    [state]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use authentication context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

// Higher-order component for protected routes
interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback = null
}) => {
  const { state } = useAuth();

  if (state.isLoading) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>Checking authentication...</p>
      </div>
    );
  }

  if (!state.isAuthenticated) {
    return fallback as React.ReactElement;
  }

  return children as React.ReactElement;
};

export default AuthContext;